/* eslint-disable indent */

import Constants from "const/constants";
import { Spinner } from "pages/common/components/state/spinners";
import {
    DND_SECTION_HANDLE_LEFT,
    DND_SECTION_HANDLE_RIGHT
} from "pages/editor/lib/timeline";

import styled, { css } from "styled-components";

export const SectionVisualizationDisplay = styled.div`
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    top: 0;
    pointer-events: none;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

export const SectionLoadingOverlay = styled(SectionVisualizationDisplay)`
    background-color: ${(props) => props.theme.colors.secondaryLight};
`;

export const SectionTemplateDisplay = styled(SectionVisualizationDisplay)`
    display: flex;
    align-items: center;
    color: white;
    background-color: ${(props) => props.theme.templateSectionBackground};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    margin: 0 10px;
`;

export const BufferTooltip = styled.div`
    color: ${(props) => props.theme.colors.backgroundGrey};
    background-color: ${(props) => props.theme.colors.backgroundLightBlack};
    display: flex;
    border-radius: 2px;
    margin-left: 2px;
`;

export const SectionDisplayThumbnail = styled(
    SectionVisualizationDisplay
).attrs(({ url }) => ({
    style: {
        backgroundImage: `url(${url})`
    }
}))`
    background-size: auto 100%;
    background-repeat: repeat-x;
    background-color: black;
`;

export const SectionUIBadgeContainer = styled.div`
    display: flex;
    position: relative;
    left: 7px;
    top: 4px;
`;

export const SectionDuration = styled.span`
    background-color: ${(props) => props.theme.colors.backgroundLightBlack};
    color: ${(props) => props.theme.colors.backgroundGrey};
    padding: 1px 2.5px;
    border-radius: 3px;
    font-size: 10px;
`;

export const SectionContextMenuButton = styled.span`
    position: absolute;
    top: 4px;
    right: 5px;
    width: 16px;
    height: 16px;
    font-size: 16px;
    color: ${(props) => props.theme.colors.backgroundGrey};
    background-color: ${(props) => props.theme.colors.backgroundLightBlack};
    cursor: pointer;
    border-radius: 2px;
    line-height: 1;

    :hover {
        background-color: ${(props) => props.theme.colors.backgroundGrey};
        color: ${(props) => props.theme.colors.backgroundLightBlack};
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
    }

    :active {
        background-color: ${(props) => props.theme.colors.outline};
    }
`;

export const CaptionsState = styled.section`
    display: flex;
    position: relative;
    background: ${(props) => props.theme.colors.backgroundLightBlack};
    border-radius: 6px;
    margin-right: 2px;

    i {
        font-size: 18px;
        color: ${(props) => props.theme.colors.backgroundGrey};
    }

    ${Spinner} {
        border-top: 5px solid #596281;
        position: relative;
        right: 2px;
        left: 0;
        top: 5px;
        margin: 0 3px 0 3px;
    }
`;

export const SectionContainer = styled.div.attrs((props) => ({
    ...(!props.isCaption &&
        props.active && {
            style: {
                borderColor: props.theme.sectionBorder
            }
        }),
    ...(props.isCaption && {
        style: {
            backgroundColor: props.theme.captionSectionBackground,
            borderColor: props.theme.captionSectionBorder
        }
    })
}))`
    position: relative;
    height: 100%;
    width: 100%;
    border-radius: 5px;
    border: 2px solid ${(props) => props.theme.assetOutline};
    background: black;
    background-position: top left;
    background-size: contain;
`;

export const SectionClickCatcher = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

export const ConnectionEl = styled.div`
    width: 2px;
    left: 0;
    height: calc(${({ height }) => Math.abs(height)}px - 4px);
    background-color: ${({ section, theme }) => {
        const { type } = section.asset;
        if (type === Constants.MEDIA_TYPE.CAPTION) {
            return theme.captionSectionBorder;
        }
        return theme.sectionBorder;
    }};
    position: absolute;

    ${({ height }) => {
        if (height < 0) {
            return css`
                bottom: 4px;
            `;
        } else {
            return css`
                top: 4px;
            `;
        }
    }}

    ${({ section, theme }) => {
        if (section.asset.type === Constants.MEDIA_TYPE.CAPTION) {
            return css`
                :after {
                    content: "";
                    width: 10px;
                    height: 6px;
                    bottom: 0;
                    position: absolute;
                    left: 0;
                    background: ${theme.captionSectionBorder};
                }
            `;
        }
    }}
`;

export const SectionHandleWrapper = styled.div`
    width: ${(props) =>
        props.type === `captions` || props.type === `audio` ? `7px` : `10px`};
    max-height: 30px;
    position: absolute;
    cursor: ew-resize;
    opacity: ${(props) => (props.isActive ? `1` : `0`)};
    top: 3px;
    bottom: 3px;
    margin: auto;
    border-radius: ${(props) => props.theme.borderRadius};
    z-index: 10000010;
    :hover {
        opacity: 1;
    }

    ${(props) => {
        switch (props.side) {
            case DND_SECTION_HANDLE_LEFT:
                return css`
                    left: ${props.type === `captions` || props.type === `audio`
                        ? `-5px`
                        : `-6px`};
                `;
            case DND_SECTION_HANDLE_RIGHT:
                return css`
                    right: ${props.type === `captions` || props.type === `audio`
                        ? `-5px`
                        : `-6px`};
                `;
        }
    }}

    ${(props) => {
        if (props.isActive && props.type !== `captions`) {
            return css`
                background-color: ${(props) => props.theme.colors.pending};
            `;
        } else if (props.isActive && props.type === `captions`) {
            return css`
                background-color: #a39eff;
            `;
        } else {
            return css`
                background-color: ${(props) =>
                    props.theme.colors.editorSectionOutline};
            `;
        }
    }}
`;

export const InnerHandleGrabber = styled.div`
    height: 80%;
    border-left: 1px solid;
    opacity: 0.3;
    top: 50%;
    transform: translateY(-50%);
    position: absolute;
    border-radius: ${(props) => props.theme.defaultBorderRadius};

    ${(props) => {
        if (props.grabber === DND_SECTION_HANDLE_LEFT) {
            return css`
                right: 3px;
            `;
        }
        if (props.grabber === DND_SECTION_HANDLE_RIGHT) {
            return css`
                left: 3px;
            `;
        }
    }}
`;
